// ═══════════════════════════════════════════════════════════════
// InspectionPanel — the inspection's home on a job. Shows what's
// been done so far and opens the wizard.
//
// Available on ANY job, at any point in its life. It used to be
// gated behind picking the "Inspection" labor preset AND behind the
// job being Started, which had the flow backwards: a tech inspects
// the car first and builds the estimate from what they find.
// ═══════════════════════════════════════════════════════════════
import React, { useState } from 'react';
import { ClipboardCheck, Camera, Play, Pencil, AlertTriangle } from 'lucide-react';
import { useInspection } from './lib/use-inspection.js';
import { useChecklists } from './lib/use-checklists.js';
import { inspectionProgress, inspectionFindings, statusMeta } from './lib/inspection-helpers.js';
import InspectionWizard from './InspectionWizard.jsx';
import InspectionPhotoReview from './InspectionPhotoReview.jsx';
import { inspectionPhotos, replaceInspectionPhoto } from './lib/inspection-photos.js';

export default function InspectionPanel({ job, vehicle }) {
  const jobId = job?.id;
  const { inspection, ready, startInspection, saveInspection } = useInspection(jobId);
  const { checklists, ready: checklistsReady } = useChecklists();
  const [wizardOpen, setWizardOpen] = useState(false);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [reviewKey, setReviewKey] = useState(null);

  const progress = inspectionProgress(inspection);
  const findings = inspectionFindings(inspection);
  const photos = inspectionPhotos(inspection);

  async function beginWith(checklist) {
    setStarting(true);
    setError(null);
    try {
      await startInspection(checklist);
      setPickerOpen(false);
      setWizardOpen(true);
    } catch (e) {
      console.error('Could not start inspection:', e);
      setError(e.message);
    } finally {
      setStarting(false);
    }
  }

  function handleStart() {
    // One checklist is the common case — don't make them pick from a
    // list of one.
    if (checklists.length === 1) return beginWith(checklists[0]);
    setPickerOpen(true);
  }

  if (!ready || !checklistsReady) {
    return (
      <section className="active-job-card inspection-panel">
        <h2 className="active-job-card-title">Vehicle inspection</h2>
        <p className="muted small">Loading…</p>
      </section>
    );
  }

  return (
    <section className="active-job-card inspection-panel">
      <div className="active-job-card-title-row">
        <h2 className="active-job-card-title">Vehicle inspection</h2>
        {inspection && (
          <span className="inspection-progress-pill">
            {progress.checked}/{progress.total} checked
          </span>
        )}
      </div>

      {error && <p className="form-error">{error}</p>}

      {!inspection && (
        <>
          <p className="muted small" style={{ marginBottom: 12 }}>
            Walk the vehicle section by section, marking each item and snapping photos as
            you go. Whatever you flag comes back as a report you can send with the estimate.
          </p>

          {pickerOpen ? (
            <div className="insp-picker">
              <p className="insp-picker-label">Which checklist?</p>
              <ul className="insp-picker-list">
                {checklists.map((c) => (
                  <li key={c.id}>
                    <button
                      type="button"
                      className="insp-picker-btn"
                      disabled={starting}
                      onClick={() => beginWith(c)}
                    >
                      <ClipboardCheck size={15} />
                      <span>
                        <strong>{c.name}</strong>
                        <span className="muted small">
                          {c.sections.length} section{c.sections.length === 1 ? '' : 's'}
                        </span>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
              <button type="button" className="btn-link" onClick={() => setPickerOpen(false)}>
                Cancel
              </button>
            </div>
          ) : (
            <button type="button" className="btn" onClick={handleStart} disabled={starting}>
              <Play size={16} strokeWidth={2.5} />
              {starting ? 'Starting…' : 'Start inspection'}
            </button>
          )}
        </>
      )}

      {inspection && (
        <>
          {findings.length > 0 ? (
            <ul className="insp-findings">
              {findings.map((f) => {
                const meta = statusMeta(f.status);
                return (
                  <li key={f.key} className={`insp-finding ${meta.cls}`}>
                    <AlertTriangle size={13} />
                    <span className="insp-finding-label">{f.label}</span>
                    {f.photos?.length > 0 && (
                      <span className="insp-finding-photos muted small">
                        <Camera size={11} /> {f.photos.length}
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="muted small" style={{ marginBottom: 12 }}>
              {progress.checked === 0
                ? 'Nothing checked yet.'
                : 'Nothing flagged — everything checked so far is OK.'}
            </p>
          )}

          <button type="button" className="btn-secondary" onClick={() => setWizardOpen(true)}>
            <Pencil size={15} />
            {progress.checked === progress.total && progress.total > 0
              ? 'Review inspection'
              : 'Continue inspection'}
          </button>
          {photos.length > 0 && (
            <button type="button" className="btn-secondary insp-review-photos"
              onClick={() => setReviewKey(photos[0].key)}>
              <Camera size={15} /> Review photos ({photos.length})
            </button>
          )}
        </>
      )}

      {wizardOpen && inspection && (
        <InspectionWizard
          jobId={jobId}
          inspection={inspection}
          vehicle={vehicle}
          onSave={saveInspection}
          onClose={() => setWizardOpen(false)}
        />
      )}
      {reviewKey && photos.length > 0 && (
        <InspectionPhotoReview photos={photos} initialKey={reviewKey} jobId={jobId}
          onClose={() => setReviewKey(null)}
          onReplace={(entry, replacement) => saveInspection(replaceInspectionPhoto(inspection, entry, replacement))}
        />
      )}
    </section>
  );
}
